import 'yup-phone'

import forEach from 'lodash/forEach'
import isFunction from 'lodash/isFunction'
import isNil from 'lodash/isNil'
import * as Yup from 'yup'
import { array, date, number, object, string } from 'yup'
import YupPassword from 'yup-password'

import { TEAM_ROLE_LABELS, TEAM_ROLE_VALUES } from './constants'
import { isFilledArray } from './helpers'
import SCHEMAS_MESSAGES from './schemasMessages'

const { INVALID, REQUIRED, SELECT, ONE_OF } = SCHEMAS_MESSAGES

YupPassword(Yup)

export const emailSchema = string()
  .email(INVALID('email address'))
  .required(REQUIRED('an email address'))

export const newPasswordSchema = string()
  .required(SCHEMAS_MESSAGES.REQUIRED('the new password'))
  .min(
    8,
    'password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special'
  )
  .minLowercase(1, 'password must contain at least 1 lower case letter')
  .minUppercase(1, 'password must contain at least 1 upper case letter')
  .minNumbers(1, 'password must contain at least 1 number')
  .minSymbols(1, 'password must contain at least 1 special character')

export const oldPasswordSchema = string()
  .required(SCHEMAS_MESSAGES.REQUIRED('the old password'))
  .min(
    8,
    'password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special'
  )
  .minLowercase(1, 'password must contain at least 1 lower case letter')
  .minUppercase(1, 'password must contain at least 1 upper case letter')
  .minNumbers(1, 'password must contain at least 1 number')
  .minSymbols(1, 'password must contain at least 1 special character')

export const emailOptionalSchema = string().email(INVALID('email address'))

export const firstNameSchema = string().required(REQUIRED('first name'))
export const lastNameSchema = string().required(REQUIRED('last name'))
export const fullNameSchema = object().shape({
  first_name: firstNameSchema,
  last_name: lastNameSchema,
})
export const packetSchema = string().required(SELECT('a packet'))
export const formSchema = string().required(SELECT('a form'))

export const packetNameSchema = string().required(REQUIRED('the packet name'))
export const newPacketFormsListSchema = array().min(
  1,
  SELECT('at least one form')
)

export const phoneSchema = string().phone('US', false, INVALID('phone number'))

export const requiredPhoneSchema = phoneSchema.required(
  REQUIRED('US phone number')
)

export const ssnSchema = string().matches(/^\d{3}-\d{2}-\d{4}$/, INVALID('SSN'))

export const requiredSsnSchema = ssnSchema.required(REQUIRED('SSN'))

export const dateOfBirthSchema = date().max(
  new Date(),
  INVALID('date of birth')
)

export const dateOfBirthRequiredSchema = date()
  .max(new Date(), INVALID('date of birth'))
  .required(REQUIRED('the date of birth'))
  .typeError(REQUIRED('the date of birth'))

export const emailOrDateOfBirth = object().shape(
  {
    email: string()
      .email()
      .when('dateOfBirth', {
        is: (dateOfBirth) => {
          return !dateOfBirth || dateOfBirth.length === 0
        },
        then: string()
          .email(INVALID('email address'))
          .required(REQUIRED('an email address or date of birth')),
        otherwise: string(),
      }),
    dateOfBirth: date().when('email', {
      is: (email) => {
        return !email || email.length === 0
      },
      then: date()
        .max(new Date(), INVALID('date of birth'))
        .required(REQUIRED('an email address or date of birth')),
      otherwise: date(),
    }),
  },
  [['email', 'dateOfBirth']]
)

export const emailOrPhoneSchema = object().shape(
  {
    email: string()
      .email()
      .when('phone', {
        is: (phone) => {
          return !phone || phone.length === 0
        },
        then: string()
          .email(INVALID('email address'))
          .required(REQUIRED('an email address or phone')),
        otherwise: string(),
      }),
    dateOfBirth: date().when('email', {
      is: (email) => {
        return !email || email.length === 0
      },
      then: date()
        .max(new Date(), INVALID('date of birth'))
        .required(REQUIRED('an email address or phone')),
      otherwise: date(),
    }),
  },
  [['email', 'phone']]
)

export const genderSchema = string()
export const providerSchema = string()

export const addressSchema = string()
export const citySchema = string()
export const stateSchema = string()
export const zipSchema = string()
export const organizationSchema = string().required(
  REQUIRED('the organization')
)
export const planSchema = string().required(SELECT('the plan'))
export const productSchema = string().required(SELECT('the product'))

export const exportNameSchema = string().required(REQUIRED('the export name'))
export const newExportQuestionListSchema = array().min(
  1,
  SELECT('at least one question')
)

export const relationshipSchema = string()

// CLIENTS
export const clientMasterSchema = object().shape({
  id: number().required(SELECT('a master client')),
})
export const clientToMergeSchema = object({
  id: number().required(SELECT('a client to merge')),
})

// TEAMS
export const teamRoleSchema = string()
  .required(REQUIRED('a role'))
  .oneOf(TEAM_ROLE_VALUES, ONE_OF('Role', TEAM_ROLE_LABELS))

// Generic schema validator
export async function schemasValidate(
  schemasConfig: [string, any, any][],
  isInvalidCallback?: Function,
  invalidLabelCallback?: Function
): boolean {
  if (!isFilledArray(schemasConfig)) return false
  let success = true

  const cbErr = isFunction(isInvalidCallback)
  const cbLbl = isFunction(invalidLabelCallback)

  for (const schemaConfig of schemasConfig) {
    if (isNil(schemaConfig?.[0]) || isNil(schemaConfig?.[2]?.validate)) {
      throw new Error(`Wrong schema config ${schemaConfig}`)
    }
    const [key, value, schema] = schemaConfig
    try {
      await schema.validate(value)
      cbErr && isInvalidCallback((e) => ({ ...e, [key]: false }))
      cbLbl && invalidLabelCallback((e) => ({ ...e, [key]: undefined }))
    } catch (validationError) {
      const { message } = validationError

      if (isFilledArray(key)) {
        forEach(key, (k) => {
          cbErr && isInvalidCallback((e) => ({ ...e, [k]: true }))
          cbLbl && invalidLabelCallback((e) => ({ ...e, [k]: message }))
        })
      } else {
        cbErr && isInvalidCallback((e) => ({ ...e, [key]: true }))
        cbLbl && invalidLabelCallback((e) => ({ ...e, [key]: message }))
      }
      success = false
    }
  }
  return success
}
